import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { modeLabel } from "../helper/repeatabilityText";

function pngObjectUrl(latin1) {
    const bytes = new Uint8Array(latin1.split("").map((char) => char.charCodeAt(0)));
    return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
}

/**
 * The photographs the repeatability was measured from: per capture mode the
 * normalization image and the plate photo of every repetition. Thumbnails;
 * ``onOpen({src, label})`` enlarges one.
 */
export default function RepeatabilityPhotoStrip({ photos, onOpen, selectionId }) {
    const [downloading, setDownloading] = useState(null);

    // The stored RAW (ARW/NEF) of one photo, as a file download.
    const downloadRaw = async (item) => {
        setDownloading(item.key);
        try {
            const response = await axios.post("api/selection/spatial/photo/download", {
                selectionId, kind: item.kind, mode: item.mode, batch: item.batch, repetition: item.repetition,
            }, { responseType: "blob", timeout: 900000 });
            const match = /filename="([^"]+)"/.exec(response.headers?.["content-disposition"] || "");
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = match ? match[1] : `${item.key}.ARW`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading the RAW:", error);
        } finally {
            setDownloading(null);
        }
    };

    const items = useMemo(() => {
        if (!photos) return [];
        const list = [];
        (photos.normalization || []).forEach((n) => list.push({
            key: `norm-${n.mode}`, kind: "normalization", mode: n.mode, label: `Normalization — ${modeLabel(n.mode)}`,
            src: pngObjectUrl(n.image), order: 0,
        }));
        (photos.plates || []).forEach((p) => list.push({
            key: `plate-${p.batch}-${p.repetition}-${p.mode}`, kind: "plate", mode: p.mode,
            batch: p.batch, repetition: p.repetition,
            label: `Repetition ${p.repetition + 1} · Batch ${p.batch + 1} — ${modeLabel(p.mode)}`,
            src: pngObjectUrl(p.image), order: 1 + p.batch * 10 + p.repetition,
        }));
        return list.sort((a, b) => a.mode - b.mode || a.order - b.order);
    }, [photos]);

    useEffect(() => () => items.forEach((item) => URL.revokeObjectURL(item.src)), [items]);

    if (!photos) {
        return <Typography variant="caption" color="text.secondary">Loading the photographs…</Typography>;
    }
    if (!items.length) return null;
    const modes = Array.from(new Set(items.map((item) => item.mode))).sort();

    return (
        <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Photographs</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                The normalization image and the plate photos the repeatability was measured from. Click to enlarge; the arrow downloads the RAW file.
            </Typography>
            <Stack spacing={1.5}>
                {modes.map((mode) => (
                    <Box key={mode}>
                        {modes.length > 1 && (
                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                {modeLabel(mode)}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            {items.filter((item) => item.mode === mode).map((item) => (
                                <Box key={item.key} sx={{ width: 160 }}>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Enlarge ${item.label}`}
                                        onClick={() => onOpen?.({ src: item.src, label: item.label })}
                                        onKeyDown={(e) => { if (e.key === "Enter") onOpen?.({ src: item.src, label: item.label }); }}
                                        sx={{
                                            width: 160, height: 120, borderRadius: 1.5, overflow: "hidden", cursor: "pointer",
                                            border: "1px solid", borderColor: "divider", bgcolor: "grey.100",
                                            "&:hover": { borderColor: "primary.main" },
                                        }}
                                    >
                                        <img src={item.src} alt={item.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                    </Box>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
                                            {item.label}
                                        </Typography>
                                        {selectionId && (
                                            <Tooltip title="Download the RAW file">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Download RAW ${item.label}`}
                                                        disabled={downloading === item.key}
                                                        onClick={() => downloadRaw(item)}
                                                    >
                                                        {downloading === item.key
                                                            ? <CircularProgress size={14} thickness={5} />
                                                            : <DownloadIcon sx={{ fontSize: 18 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
